import type { NotebookFile } from './hooks/useGetAllFiles';
import type { FileProcessingEntry } from './hooks/useFileProcessingStatus';

export type FileStatusKind =
  | 'uploading'
  | 'queued'
  | 'indexing'
  | 'delayed'
  | 'failed'
  | 'ready'
  | 'not_indexable'
  | 'skipped';

export type FileStatusView = {
  kind: FileStatusKind;
  label: string;
  tooltip: string;
  /** Bootstrap background class for the badge. */
  tone: 'bg-success' | 'bg-info' | 'bg-warning' | 'bg-danger' | 'bg-secondary';
  /** 0-100 for a determinate bar; null for none. */
  progress: number | null;
  /** Show an indeterminate (striped, animated) bar. */
  busy: boolean;
  canRetry: boolean;
};

const isTempFileId = (id: string) => id.startsWith('temp-');

/**
 * One status per file, in the order a user experiences it: uploading (bar with
 * %), queued, indexing (bar with % when chunk counts are known), then "Ready
 * for Pratus AI", "Not searchable" or "Failed" with a retry. Viewing and
 * downloading work as soon as the upload finishes, whatever this says.
 */
export function resolveFileStatus(
  file: Pick<
    NotebookFile,
    'id' | 'fileUrl' | 'rag_ready' | 'rag_status' | 'rag_error'
  >,
  entry: FileProcessingEntry | undefined,
  uploadPercent: number | undefined
): FileStatusView {
  if (isTempFileId(file.id) || !file.fileUrl) {
    return {
      kind: 'uploading',
      label: `Uploading ${Math.round(uploadPercent ?? 0)}%`,
      tooltip: 'Uploading. You can open the file once this finishes.',
      tone: 'bg-info',
      progress: uploadPercent ?? 0,
      busy: false,
      canRetry: false,
    };
  }

  if (file.rag_status === 'skipped') {
    return {
      kind: 'skipped',
      label: '',
      tooltip: 'This file was uploaded without indexing for Pratus AI.',
      tone: 'bg-secondary',
      progress: null,
      busy: false,
      canRetry: false,
    };
  }

  if (
    file.rag_status === 'not_indexable' ||
    entry?.status === 'not_indexable'
  ) {
    return {
      kind: 'not_indexable',
      label: 'Not searchable',
      tooltip:
        'No text could be read from this file (for example a scanned image), so Pratus AI cannot search it. You can still open it.',
      tone: 'bg-secondary',
      progress: null,
      busy: false,
      canRetry: false,
    };
  }

  if (file.rag_ready) {
    return {
      kind: 'ready',
      label: 'Ready for Pratus AI',
      tooltip: 'Pratus AI can search this file.',
      tone: 'bg-success',
      progress: null,
      busy: false,
      canRetry: false,
    };
  }

  const status =
    entry?.status ?? (file.rag_status === 'failed' ? 'failed' : undefined);

  if (status === 'failed') {
    const reason = entry?.error ?? file.rag_error ?? 'Indexing failed.';
    return {
      kind: 'failed',
      label: "Couldn't prepare for Pratus AI",
      tooltip: `${reason} You can still open the file.`,
      tone: 'bg-danger',
      progress: null,
      busy: false,
      canRetry: true,
    };
  }

  if (status === 'delayed') {
    return {
      kind: 'delayed',
      label: 'Delayed: waiting for processing',
      tooltip:
        'Queued for indexing, but indexing is not running right now. It will start automatically; you can still open the file.',
      tone: 'bg-warning',
      progress: null,
      busy: false,
      canRetry: false,
    };
  }

  if (status === 'processing') {
    const progress = entry?.progress ?? null;
    return {
      kind: 'indexing',
      label:
        progress != null
          ? `Preparing for Pratus AI ${progress}%`
          : 'Preparing for Pratus AI',
      tooltip: 'Preparing this file for Pratus AI. You can already open it.',
      tone: 'bg-info',
      progress,
      busy: progress == null,
      canRetry: false,
    };
  }

  return {
    kind: 'queued',
    label: 'Preparing for Pratus AI',
    tooltip:
      'Waiting to be indexed for Pratus AI. You can already open the file.',
    tone: 'bg-info',
    progress: null,
    busy: false,
    canRetry: false,
  };
}
